import { FC, useEffect, useId, useState } from "react";
import {
	Calendar,
	Check,
	Circle,
	ClipboardList,
	ExternalLink,
	FileText,
	Hash,
	Link2,
	MinusCircle,
	Newspaper,
	Search,
	Smartphone,
	X
} from "lucide-react";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { RadioGroup, RadioGroupCard } from "src/components/ui/radio-group";
import { SEARCH_MIN_CHARS } from "src/constants";
import { cn } from "src/lib/utils";
import { useDebounce } from "src/hooks/useDebounce";
import { useHashtags } from "src/features/Hashtags/hooks/useHashtags";
import PickerLoadError from "src/components/SearchPicker/PickerLoadError";
import SearchMinCharsHint from "src/components/SearchPicker/SearchMinCharsHint";
import { useRefetchOnRetype } from "src/components/SearchPicker/useRefetchOnRetype";

export type LinkType =
	| "none"
	| "webview"
	| "external"
	| "hashtag"
	| "page"
	| "news"
	| "events"
	| "forms";

export interface LinkPickerHashtag {
	uuid: string;
	name: string;
}

export interface LinkPickerValue {
	type: LinkType;
	url?: string | null;
	hashtag?: LinkPickerHashtag | null;
	pageId?: string | null;
	pageName?: string | null;
	newsId?: string | null;
	newsTitle?: string | null;
	eventId?: string | null;
	eventName?: string | null;
	formId?: string | null;
	formName?: string | null;
}

interface Props {
	value: LinkPickerValue;
	onChange: (next: LinkPickerValue) => void;
	label?: string;
	helperText?: string;
	disabledTypes?: LinkType[];
	/**
	 * When true, visually hides the legend/helper text but keeps them in
	 * the accessibility tree. Use this when the surrounding card already
	 * provides a visible title (matches the rest of the admin's "title
	 * outside, controls inside" pattern).
	 */
	hideLabel?: boolean;
}

const TYPES: {
	value: LinkType;
	label: string;
	description: string;
	icon: typeof Link2;
}[] = [
	{
		value: "none",
		label: "No link",
		description: "Display only",
		icon: MinusCircle
	},
	{
		value: "webview",
		label: "In-app link",
		description: "Opens in webview",
		icon: Smartphone
	},
	{
		value: "external",
		label: "External link",
		description: "Opens in browser",
		icon: ExternalLink
	},
	{
		value: "hashtag",
		label: "Hashtag",
		description: "Jump to a social feed",
		icon: Hash
	},
	{
		value: "page",
		label: "Page",
		description: "Link to a page",
		icon: FileText
	},
	{
		value: "news",
		label: "News",
		description: "Link to a news article",
		icon: Newspaper
	},
	{
		value: "events",
		label: "Events",
		description: "Link to an event",
		icon: Calendar
	},
	{
		value: "forms",
		label: "Forms",
		description: "Link to a form",
		icon: ClipboardList
	}
];

// Accept anything that looks roughly like a URL: bare domain, www., or full
// http(s) scheme. Validation is intentionally forgiving — strict parsing
// happens on blur via normaliseUrl().
const URL_LOOSE = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(:\d+)?(\/[^\s]*)?$/i;

// Schemes we explicitly refuse to ever accept. `javascript:` and friends are
// classic XSS sinks if a downstream caller binds the URL to an `href` or
// webview opener.
export const FORBIDDEN_SCHEME = /^(javascript|data|vbscript|file|blob):/i;

export function looksLikeUrl(raw: string): boolean {
	if (!raw) return false;
	const trimmed = raw.trim();
	if (!trimmed) return false;
	if (/\s/.test(trimmed)) return false;
	if (FORBIDDEN_SCHEME.test(trimmed)) return false;
	return URL_LOOSE.test(trimmed);
}

// Normalise on blur:
//   "google.com"               -> "https://google.com"
//   "www.google.com"           -> "https://www.google.com"
//   "http://foo.com"           -> "http://foo.com" (unchanged)
//   "[text](https://foo.com)"  -> "https://foo.com" (strip markdown link wrap)
//   "  https://foo.com  "      -> "https://foo.com"
export function normaliseUrl(raw: string): string {
	let trimmed = raw.trim();
	if (!trimmed) return "";
	const markdownMatch = trimmed.match(/^\[[^\]]*\]\((.+)\)$/);
	if (markdownMatch) trimmed = markdownMatch[1].trim();
	if (FORBIDDEN_SCHEME.test(trimmed)) return "";
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	if (looksLikeUrl(trimmed)) return `https://${trimmed}`;
	return trimmed;
}

const LinkPicker: FC<Props> = ({
	value,
	onChange,
	label = "Link",
	helperText = "Choose one. Switching options replaces your current selection.",
	disabledTypes = [],
	hideLabel = false
}) => {
	const radiogroupId = useId();
	const listboxId = useId();
	const [hashtagSearch, setHashtagSearch] = useState("");
	const debouncedSearch = useDebounce(hashtagSearch, 250);

	const hashtagQuery = useHashtags(debouncedSearch, {
		enabled: value.type === "hashtag"
	});
	const { data, isFetching } = hashtagQuery;
	// The same failed-search recovery the page/news/event/form pickers share
	// (SearchPicker): retyping the failed term, or Try again, fetches it again.
	const { markTyped } = useRefetchOnRetype(
		hashtagSearch,
		debouncedSearch,
		hashtagQuery
	);
	// One measure of "enough to search", shared with SearchPicker: the
	// trimmed term (useHashtags trims too). The live term hides results the
	// moment the field drops below it, not a debounce later.
	const liveHashtagTermLength = hashtagSearch.trim().length;
	const hashtagSearchable =
		debouncedSearch.trim().length >= SEARCH_MIN_CHARS &&
		liveHashtagTermLength >= SEARCH_MIN_CHARS;
	const hashtagSearchFailed =
		hashtagSearchable && Boolean(hashtagQuery.isError) && !isFetching;
	const showHashtagHint =
		liveHashtagTermLength < SEARCH_MIN_CHARS &&
		(!value.hashtag || liveHashtagTermLength > 0);
	const hashtagResults = hashtagSearchable
		? (data?.pages
				.flatMap((p) => p.results)
				.filter((h) => h.uuid !== value.hashtag?.uuid) ?? [])
		: [];

	const types = TYPES.filter((t) => !disabledTypes.includes(t.value));

	// If the parent disables the currently-selected type at runtime, fall back
	// to "none" so the UI stays in a coherent state.
	// `disabledTypes` is commonly an inline array literal at the call-site,
	// so it changes identity every parent render. We stringify-compare to
	// avoid the effect re-firing on every render — only on actual content
	// change.
	const disabledKey = disabledTypes.join(",");
	useEffect(() => {
		if (value.type !== "none" && disabledTypes.includes(value.type)) {
			onChange({ type: "none", url: null, hashtag: null });
		}
		// onChange is intentionally omitted — callers commonly inline a new
		// arrow function and we don't want to re-run on every render.
	}, [disabledKey, value.type]);

	const handleTypeChange = (next: LinkType) => {
		// Radix RadioGroup occasionally emits an empty string when the group
		// is cleared programmatically — guard against any value not in the
		// closed union.
		if (!TYPES.some((t) => t.value === next)) return;
		if (next === "none") {
			onChange({
				type: "none",
				url: null,
				hashtag: null,
				pageId: null,
				pageName: null
			});
			return;
		}
		if (next === "webview" || next === "external") {
			onChange({
				type: next,
				url: value.url ?? "",
				hashtag: null,
				pageId: null,
				pageName: null
			});
			return;
		}
		if (next === "hashtag") {
			onChange({
				type: "hashtag",
				url: null,
				hashtag: value.hashtag ?? null,
				pageId: null,
				pageName: null
			});
			return;
		}
		if (next === "page") {
			onChange({
				type: "page",
				url: null,
				hashtag: null,
				pageId: value.pageId ?? null,
				pageName: value.pageName ?? null
			});
			return;
		}
		if (next === "news") {
			onChange({
				type: "news",
				url: null,
				hashtag: null,
				pageId: null,
				pageName: null,
				newsId: value.newsId ?? null,
				newsTitle: value.newsTitle ?? null
			});
			return;
		}
		if (next === "events") {
			onChange({
				type: "events",
				url: null,
				hashtag: null,
				pageId: null,
				pageName: null,
				eventId: value.eventId ?? null,
				eventName: value.eventName ?? null
			});
			return;
		}
		if (next === "forms") {
			onChange({
				type: "forms",
				url: null,
				hashtag: null,
				pageId: null,
				pageName: null,
				formId: value.formId ?? null,
				formName: value.formName ?? null
			});
		}
	};

	const handleUrlChange = (next: string) => {
		// Reject forbidden schemes the moment they're typed — never let
		// `javascript:`, `data:`, etc. enter form state, regardless of
		// whether blur/paste ever fires.
		if (FORBIDDEN_SCHEME.test(next.trim())) {
			onChange({ ...value, url: "" });
			return;
		}
		onChange({ ...value, url: next });
	};

	const handleUrlBlur = () => {
		const raw = value.url ?? "";
		const normalised = normaliseUrl(raw);
		// No-op when the user tabbed through an empty field — avoids flipping
		// `null` (untouched) to `""` (cleared) and false-positives in parent
		// dirty-state tracking.
		if (normalised === raw) return;
		onChange({ ...value, url: normalised });
	};

	const handleUrlPaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
		const pasted = e.clipboardData.getData("text");
		if (!pasted) return;
		const normalised = normaliseUrl(pasted);
		if (normalised !== pasted) {
			e.preventDefault();
			onChange({ ...value, url: normalised });
		}
	};

	const handleHashtagSelect = (hashtag: LinkPickerHashtag) => {
		onChange({ type: "hashtag", url: null, hashtag });
		setHashtagSearch("");
	};

	const handleHashtagClear = () => {
		onChange({ type: "hashtag", url: null, hashtag: null });
		setHashtagSearch("");
	};

	const urlIsInvalid =
		(value.type === "webview" || value.type === "external") &&
		Boolean(value.url?.trim()) &&
		!looksLikeUrl(value.url ?? "");

	const summary = (() => {
		switch (value.type) {
			case "none":
				return (
					<>
						This will be <strong>display only</strong> — tapping does nothing.
					</>
				);
			case "webview":
				return value.url ? (
					<>
						Opens <strong>{value.url}</strong> inside the app and website.
					</>
				) : (
					<>Add a URL to open inside the app and website.</>
				);
			case "external":
				return value.url ? (
					<>
						Opens <strong>{value.url}</strong> in the system browser.
					</>
				) : (
					<>Add a URL to open in the system browser.</>
				);
			case "hashtag":
				return value.hashtag ? (
					<>
						Opens the <strong>#{value.hashtag.name}</strong> social feed.
					</>
				) : (
					<>Pick a hashtag to link to its social feed.</>
				);
			case "page":
				return value.pageName ? (
					<>
						Links to the <strong>{value.pageName}</strong> page.
					</>
				) : (
					<>Pick a page to link to.</>
				);
			case "news":
				return value.newsTitle ? (
					<>
						Links to the <strong>{value.newsTitle}</strong> news article.
					</>
				) : (
					<>Pick a news article to link to.</>
				);
			case "events":
				return value.eventName ? (
					<>
						Links to the <strong>{value.eventName}</strong> event.
					</>
				) : (
					<>Pick an event to link to.</>
				);
			case "forms":
				return value.formName ? (
					<>
						Links to the <strong>{value.formName}</strong> form.
					</>
				) : (
					<>Pick a form to link to.</>
				);
			default: {
				// Exhaustiveness check — adding a new LinkType without updating
				// this switch will fail to compile.
				((_: never) => _)(value.type);
				return null;
			}
		}
	})();

	return (
		<div className="flex flex-col gap-4">
			<fieldset>
				<legend className={cn(hideLabel ? "sr-only" : "text-label mb-1")}>
					{label}
				</legend>
				<p
					className={cn(
						hideLabel ? "sr-only" : "text-caption text-muted-foreground mb-3"
					)}
				>
					{helperText}
				</p>
				<RadioGroup
					id={radiogroupId}
					aria-label={label}
					value={value.type}
					onValueChange={(next) => handleTypeChange(next as LinkType)}
					className="flex flex-col gap-2"
				>
					{types.map((option) => {
						const active = value.type === option.value;
						const Icon = option.icon;
						return (
							<RadioGroupCard
								key={option.value}
								value={option.value}
								className="flex-row items-center gap-3"
							>
								<Icon
									className={cn(
										"h-5 w-5 shrink-0",
										active ? "text-link" : "text-muted-foreground"
									)}
									aria-hidden="true"
								/>
								<span className="flex min-w-0 flex-1 flex-col">
									<span
										className={cn(
											"text-body",
											active ? "text-link" : "text-foreground"
										)}
									>
										{option.label}
									</span>
									<span className="text-caption text-muted-foreground">
										{option.description}
									</span>
								</span>
								{active ? (
									<Check
										className="h-4 w-4 shrink-0 text-link"
										aria-hidden="true"
									/>
								) : (
									<Circle
										className="h-4 w-4 shrink-0 text-zinc-300"
										aria-hidden="true"
									/>
								)}
							</RadioGroupCard>
						);
					})}
				</RadioGroup>
			</fieldset>

			<p
				className="text-caption text-muted-foreground"
				aria-live="polite"
				data-testid="link-picker-summary"
			>
				{summary}
			</p>

			{(value.type === "webview" || value.type === "external") && (
				<div className="flex flex-col gap-2">
					<Label htmlFor={`${radiogroupId}-url`}>URL</Label>
					<Input
						id={`${radiogroupId}-url`}
						type="text"
						inputMode="url"
						autoComplete="off"
						spellCheck={false}
						placeholder="google.com"
						value={value.url ?? ""}
						onChange={(e) => handleUrlChange(e.target.value)}
						onBlur={handleUrlBlur}
						onPaste={handleUrlPaste}
						aria-invalid={urlIsInvalid}
						aria-describedby={
							urlIsInvalid ? `${radiogroupId}-url-error` : undefined
						}
					/>
					{urlIsInvalid && (
						<p
							id={`${radiogroupId}-url-error`}
							role="alert"
							className="text-caption text-destructive"
						>
							That doesn&apos;t look like a web address. Try something like
							google.com or https://www.google.com
						</p>
					)}
					{value.type === "webview" && (
						<p className="text-caption text-muted-foreground">
							The user stays inside the app or website. SSO is forwarded
							automatically for internal pages.
						</p>
					)}
					{value.type === "external" && (
						<p className="text-caption text-muted-foreground">
							Opens in the device&apos;s default browser. Use this for public or
							non-SSO pages.
						</p>
					)}
				</div>
			)}

			{value.type === "hashtag" && (
				<div className="flex flex-col gap-3">
					{value.hashtag && (
						<div className="flex flex-wrap gap-1.5">
							<span className="inline-flex items-center gap-1.5 rounded-full bg-primary-light text-primary text-caption px-3 py-0.5">
								#{value.hashtag.name}
								<button
									type="button"
									aria-label={`Remove #${value.hashtag.name}`}
									onClick={handleHashtagClear}
									className="rounded-full transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-primary-light"
								>
									<X className="h-3 w-3" />
								</button>
							</span>
						</div>
					)}
					<div className="relative">
						<Search
							size={16}
							aria-hidden="true"
							className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
						/>
						<Input
							type="search"
							value={hashtagSearch}
							onChange={(e) => {
								markTyped();
								setHashtagSearch(e.target.value);
							}}
							placeholder="Search hashtags…"
							aria-label="Search hashtags"
							aria-describedby={[
								`${listboxId}-status`,
								showHashtagHint && `${listboxId}-hint`,
								hashtagSearchFailed && `${listboxId}-error`,
								hashtagSearchable &&
									!isFetching &&
									!hashtagSearchFailed &&
									hashtagResults.length === 0 &&
									`${listboxId}-empty`
							]
								.filter(Boolean)
								.join(" ")}
							className="pl-8"
						/>
					</div>
					{/* Status region — announces result count / empty state to AT
					    without claiming the combobox/listbox pattern. */}
					<p
						id={`${listboxId}-status`}
						role="status"
						aria-live="polite"
						className="sr-only"
					>
						{hashtagSearchable && isFetching && "Searching…"}
						{hashtagSearchable &&
							!isFetching &&
							!hashtagSearchFailed &&
							(hashtagResults.length === 0
								? "No hashtags found"
								: `${hashtagResults.length} hashtag${hashtagResults.length === 1 ? "" : "s"} found`)}
					</p>
					{hashtagSearchable && hashtagResults.length > 0 && (
						<div
							id={listboxId}
							className="border border-border rounded-lg bg-background shadow-sm max-h-[240px] overflow-y-auto flex flex-col"
						>
							{hashtagResults.map((hashtag) => (
								<button
									key={hashtag.uuid}
									type="button"
									onClick={() =>
										handleHashtagSelect({
											uuid: hashtag.uuid,
											name: hashtag.name
										})
									}
									className="flex items-center gap-2 w-full px-3 py-2 text-body hover:bg-muted focus-visible:outline-none focus-visible:bg-muted text-left"
								>
									<Hash
										className="h-4 w-4 text-muted-foreground"
										aria-hidden="true"
									/>
									<span className="text-body text-foreground">
										{hashtag.name}
									</span>
								</button>
							))}
						</div>
					)}
					{hashtagSearchFailed && (
						<PickerLoadError
							id={`${listboxId}-error`}
							noun="hashtags"
							onRetry={hashtagQuery.refetch}
						/>
					)}
					{hashtagSearchable &&
						!isFetching &&
						!hashtagSearchFailed &&
						hashtagResults.length === 0 && (
							<p
								id={`${listboxId}-empty`}
								className="text-caption text-muted-foreground"
							>
								No hashtags found
							</p>
						)}
					{showHashtagHint && <SearchMinCharsHint id={`${listboxId}-hint`} />}
				</div>
			)}
		</div>
	);
};

export default LinkPicker;
